import { convertToLocalTime, convertToLocalTimeUnix } from '@/composable/useFormatDateAndHours'
import { computed, ref } from 'vue'
import { useChartExport, type ChartExportConfig } from './useChartExport'

export interface LineChartConfig {
  chartId: string
  exportConfig: ChartExportConfig
  yAxisFormatter?: (value: number) => string
  tooltipYFormatter?: (value: number) => string
  unit?: string
}

export interface TimeRange {
  min?: number
  max?: number
}

/**
 * Composable base para gráficos de linha com datetime
 */
export function useLineChart(config: LineChartConfig) {
  const series = ref<any[]>([])
  const { getToolbarConfig } = useChartExport(config.exportConfig)

  const chartOptions = computed(() => ({
    legend: {
      show: true,
      position: 'top' as const,
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    chart: {
      type: 'line' as const,
      id: config.chartId,
      fontFamily: 'Satoshi, sans-serif',
      toolbar: getToolbarConfig(),
    },
    stroke: {
      width: 2,
      curve: 'straight' as const,
    },
    yaxis: {
      labels: {
        formatter: config.yAxisFormatter || ((value: number) => `${value?.toFixed(2)} ${config.unit || ''}`),
      },
      style: {
        fontSize: '14px',
        fontWeight: 'regular' as const,
        colors: ['#000'],
      },
    },
    xaxis: {
      type: 'datetime' as const,
      style: {
        fontSize: '14px',
        fontWeight: 'regular' as const,
        colors: ['#000'],
      },
      labels: {
        rotate: 0,
        show: true,
        format: 'dd/MM/yyyy HH:mm',
        hideOverlappingLabels: true,
      },
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      style: {
        fontSize: '14px',
        fontWeight: 'bold' as const,
        colors: ['#fff'],
      },
      fixed: {
        enabled: true,
        position: 'topRight' as const,
        offsetX: 0,
        offsetY: -100,
      },
      x: {
        format: 'dd/MM/yyyy HH:mm',
      },
      y: {
        formatter: config.tooltipYFormatter || ((val: number) => `${val?.toFixed(2)} ${config.unit || ''}`),
      },
    },
  }))

  const setTimeRange = (startDate?: Date, endDate?: Date) => {
    const timeRange: TimeRange = {
      min: startDate ? convertToLocalTimeUnix(startDate) : undefined,
      max: endDate ? convertToLocalTimeUnix(endDate) : undefined,
    }

    // Atualizar o xaxis com o novo range
    chartOptions.value.xaxis = {
      ...chartOptions.value.xaxis,
      ...timeRange,
    }
  }

  const updateSeries = (newSeries: any[]) => {
    series.value = newSeries
  }

  const processReadings = (readings: any[], timeField: string = 'time', valueField: string = 'value') => {
    return (
      readings?.map((reading) => ({
        x: convertToLocalTime(reading[timeField]),
        y: parseFloat(reading[valueField]),
      })) || []
    )
  }

  return {
    series,
    chartOptions,
    setTimeRange,
    updateSeries,
    processReadings,
  }
}
